import { DialogType } from '@tableau/widgets';
import * as React from 'react';
import { Provider } from 'react-redux';
import * as _ from 'underscore';

import { RefreshSelfServiceScheduleDialog } from '~src/app/content/RefreshSelfServiceScheduleDialog';
import { openTabbedScheduleDialogWithSSS, TabbedScheduleDialogWithSSSProps } from '~src/app/content/TabbedScheduleDialogWithSSS';
import { UrlParamKeys } from '~src/app/UrlParamKeys';
import { SimpleDeferred } from '~src/app/utils/SimpleDeferred';
import { SearchSuggestionsService } from '~src/content/services/SearchSuggestionsService';
import { CreateDatasourceExtractTaskActionService } from '~src/extract/actions/CreateDatasourceExtractTaskAction';
import { CreateExtractTaskActionService, CreateExtractTasksMode, IExtractTaskSchedule } from '~src/extract/actions/CreateExtractTaskAction';
import { CreateWorkbookExtractTaskActionService } from '~src/extract/actions/CreateWorkbookExtractTaskAction';
import { IResult } from '~src/httpClient/serverApi/types/IResponse';
import { ScheduleActions } from '~src/schedule/types/ISchedule';
import { formatListAsString } from '~src/schedule/utils/ListToStringFormatter';
import { Helpers } from '~src/ts/helpers/Helpers';
import { getQueryFromUrl } from '~src/ts/helpers/UrlHelpers';

import { createExtractRefresh } from '../../app/actions/spaces/CreateExtractRefreshAction';
import { incrementRouterSpaceReactKey } from '../../app/actions/spaces/IncrementRouterSpaceReactKeyAction';
import { shareContent } from '../../app/actions/spaces/ShareContentAction';
import { getAppStore } from '../../app/AppStore';
import { PublishCompleteDialog, PublishCompleteDialogProps } from '../../app/content/PublishCompleteDialog';
import {
  createSelfServiceScheduleExtractAction,
} from '../../app/sagas/spaces/extractTasks/CreateExtractRefreshSaga';
import { SessionSiteSelectors } from '../../app/selectors/SessionInfoSelectors';
import { Messages } from '../../codegen/Localize';
import { PublishCompleteDialogDisplayedEvent, TelemetryEvents } from '../../common/telemetryEvents/TelemetryEvents';
import { ModalInstance, ReactModalService } from '../../commonInfra/dialogs/ReactModalService';
import { Telemetry } from '../../commonInfra/services/Telemetry';
import { IDataSourceBase } from '../../datasource/models/IDataSourceInfo';
import { DataSources, RefreshOptionType } from '../../datasource/resources/Datasources';
import { ChangeDatasourceRefreshModeAction } from '../../datasource/services/ChangeDatasourceRefreshModeAction';
import { ISyncPublishResult } from '../../datasources/services/DatasourcePublishResultService';
import { DevicePreviewModalBuilder } from '../../devicePreview/utils/DevicePreviewModalBuilder';
import { ExtractTasks } from '../../extract/resources/ExtractTasks';
import { ServerSettingsUnauthenticated } from '../../settings/services/ServerSettingsUnauthenticated';
import { Views } from '../../views/resources/Views';
import { IWorkbookDetailInfo } from '../../workbook/models/IWorkbookInfo';
import { Workbooks } from '../../workbook/resources/Workbooks';
import { FeatureFlags } from '../FeatureFlags';
import { Filter } from '../helpers/Filter';
import { Order } from '../helpers/Order';
import { SliceableHelper } from '../helpers/SliceableHelper';
import { L10n } from '../L10n/L10n';
import { IContentInfo } from '../models/IContentInfo';
import { IViewInfo } from '../models/IViewInfo';
import { RemoteRefreshAgents } from '../resources/RemoteRefreshAgents';
import { FilterFieldNames } from '../ServerApi/types/IFilterClause';
import { RefreshModeTypes } from '../ServerApi/types/IRemoteRefreshProperties';
import { PublishDatasourceAction } from './PublishDatasourceAction';
import { ToasterService, ToastType } from './ToasterService';

export enum PostPublishStep {
  Done,
  Schedule,
  LiveQuerySync
}

export interface IDatasourceTab {
  datasource: IDataSourceBase;
  extractTask?: IExtractTaskSchedule;
  iconClassFn: () => string;
}

export interface IWorkbookTab {
  workbook: IWorkbookDetailInfo;
  extractTask?: IExtractTaskSchedule;
  iconClassFn: () => string;
}

export namespace PublishWorkbookAction {
  let dialogWithPreviewModalInstance: ModalInstance<unknown>;

  export function handlePublishedWorkbook(workbookDetailInfo: IWorkbookDetailInfo, resultUrl: string) {
    const search = getQueryFromUrl(resultUrl);

    const publishResult: ISyncPublishResult = {
      isSyncAvailable: Helpers.BooleanQueryParamRegex.test(search[UrlParamKeys.SyncAvailable]),
      isSyncRunning: Helpers.BooleanQueryParamRegex.test(search[UrlParamKeys.SyncRunning]),
      remoteAgentWorkflowId: search[UrlParamKeys.RemoteAgentWorkflowId],
      publishedWithLQS: Helpers.BooleanQueryParamRegex.test(search[UrlParamKeys.PublishedWithLQS])
    };

    const publishedDatasourcesStr = search[UrlParamKeys.PublishedDataSources];
    const publishedDatasourceUrlNames = publishedDatasourcesStr ? publishedDatasourcesStr.split(',') : [];

    const publishedDatasourcesLqsStr = search[UrlParamKeys.PublishedDataSourcesWithLQS];
    const publishedDatasourceLQSUrlNames = publishedDatasourcesLqsStr ? publishedDatasourcesLqsStr.split(',') : [];

    showDialogBasedOnResult(workbookDetailInfo, publishedDatasourceUrlNames, publishedDatasourceLQSUrlNames, publishResult);
  }

  function showDialogBasedOnResult(workbook: IWorkbookDetailInfo, publishedDatasourceUrlNames: string[], publishedDatasourcesWithLqsUrlNames: string[], publishResult: ISyncPublishResult) {

    // Make our best attempt to fire this off, even when the window closes.
    const unloadListener = () =>
      RemoteRefreshAgents.completeWorkflowDuringWindowUnload(publishResult.remoteAgentWorkflowId);
    window.addEventListener('unload', unloadListener, false);

    // Don't attempt to look up remote agent count if datasync isn't enabled on this server.
    const remoteAgentsPromise = ServerSettingsUnauthenticated.isBridgeEnabled() ?
      RemoteRefreshAgents.get() : Promise.resolve({});

    Promise.all([
      getWorkbookExtractTaskCount(workbook),
      DataSources.getPublishedByUrlNames(publishedDatasourceUrlNames),
      DataSources.getPreferredRefreshOptionsByUrlName(publishedDatasourceUrlNames),
      Workbooks.isWorkbookServerRefreshable(workbook.id),
      remoteAgentsPromise
    ]).then((results: any[]): Promise<any> => {
        const workbookExtractTaskCount = results[0];
        const datasources = results[1];
        const preferredRefreshOptions = results[2];
        const isWorkbookServerRefreshable = results[3] && results[3].refreshable;
        const hasRemoteAgents =  ServerSettingsUnauthenticated.isBridgeEnabled() && !_.isEmpty(results[4].agents);

        const shouldRefreshWorkbook = (workbookExtractTaskCount === 0 && isWorkbookServerRefreshable);
        const serverRefreshDatasources: IDataSourceBase[] = [];
        const remoteRefreshDatasources: IDataSourceBase[] = [];
        const remoteLiveSyncDatasources: IDataSourceBase[] = [];

        _.forEach(datasources.items, (datasource: IDataSourceBase) => {
          const refreshOptions = preferredRefreshOptions[datasource.urlName];
          if (refreshOptions && refreshOptions.suggestion) {
            switch (refreshOptions.suggestion) {
              case RefreshOptionType.Server:
                serverRefreshDatasources.push(datasource);
                break;
              case RefreshOptionType.OnlineSync:
                if (hasRemoteAgents || PublishDatasourceAction.canReserveSyncWithWorkflow(publishResult)) {
                  remoteRefreshDatasources.push(datasource);
                }
                break;
              case RefreshOptionType.LiveConnection:
                if(publishedDatasourcesWithLqsUrlNames.indexOf(datasource.urlName) !== -1 && hasRemoteAgents) {
                  remoteLiveSyncDatasources.push(datasource);
                }
                break;
            }
          }
        });

      // There are six different cases here:
      // Case 1: There's nothing to schedule, so show a "Done" button that closes the dialog. (This includes "remote refreshes required, but no agents are available." and "The workbook already has a schedule.")
      // Case 2: Only the workbook needs a schedule, so show the workbook extract refresh schedule dialog.
      // Case 3: Only a single datasource needs a schedule, and it's a server refresh, so show the datasource extract server-refresh schedule dialog.
      // Case 4: Only a single datasource needs a schedule, and it's a remote refresh, so show the datasource extract remote-refresh schedule dialog.
      // Case 5: There are just live query sync datasources, so show the corresponding text
      // Case 6: More than one thing needs a schedule or an agent, so show the tabbed schedule interface with those things.
      if (!shouldRefreshWorkbook && serverRefreshDatasources.length === 0 && remoteRefreshDatasources.length === 0 && remoteLiveSyncDatasources.length === 0) {
        // Case 1: There's nothing to schedule, so show a "Done" button that closes the dialog.
        // (This includes "remote refreshes required, but no agents are available." and "The workbook already has a schedule.")

        return displaySuccessDialog(workbook, datasources.items, PostPublishStep.Done);

      } else if (shouldRefreshWorkbook && serverRefreshDatasources.length === 0 && remoteRefreshDatasources.length === 0 && remoteLiveSyncDatasources.length === 0) {
        // Case 2: Only the workbook needs a schedule, so show the workbook extract refresh schedule dialog.

        return displaySuccessDialog(workbook, datasources.items, PostPublishStep.Schedule)
          .then(() => CreateWorkbookExtractTaskActionService.forWorkbook(workbook, CreateExtractTasksMode.AllowScheduleOnServer))
          .then(() => getAppStore().dispatch(incrementRouterSpaceReactKey()));

      } else if (!shouldRefreshWorkbook && serverRefreshDatasources.length === 1 && remoteRefreshDatasources.length === 0 && remoteLiveSyncDatasources.length === 0) {
        // Case 3: Only a single datasource needs a schedule, and it's a server refresh, so show the datasource extract server-refresh schedule dialog.

        const datasource = serverRefreshDatasources[0];
        return displaySuccessDialog(workbook, datasources.items, PostPublishStep.Schedule)
          .then(() => changeDSRefreshMode(datasource, RefreshModeTypes.Server))
          .then(() => CreateDatasourceExtractTaskActionService.forDatasource(datasource, CreateExtractTasksMode.AllowScheduleOnServer))
          .then(() => getAppStore().dispatch(incrementRouterSpaceReactKey()));

      } else if (!shouldRefreshWorkbook && serverRefreshDatasources.length === 0 && remoteRefreshDatasources.length === 1 && remoteLiveSyncDatasources.length === 0) {
        // Case 4: Only a single datasource needs a schedule, and it's a remote refresh, so show the datasource extract remote-refresh schedule dialog.

        const datasource = remoteRefreshDatasources[0];

        if (FeatureFlags.selfServiceSchedulesForRefreshEnabled && SessionSiteSelectors.isSelfServiceSchedulesForRefreshEnabled(getAppStore().getState())) {
          if (datasource.isFileBased) {
            return displaySuccessDialog(workbook, datasources.items, PostPublishStep.Schedule)
              .then(() => ChangeDatasourceRefreshModeAction.execute(datasource, RefreshModeTypes.Remote, null, true, false))
              .then(() => CreateDatasourceExtractTaskActionService.forDatasource(datasource, CreateExtractTasksMode.AllowScheduleOnlyOnClient)
                .then(() => getAppStore().dispatch(incrementRouterSpaceReactKey())));
          } else {
            const contentInfo = [datasource] as IContentInfo[];
            const action = createExtractRefresh(contentInfo, CreateExtractTasksMode.AllowScheduleOnServer);
            return displaySuccessDialog(workbook, datasources.items, PostPublishStep.Schedule)
              .then(() => ChangeDatasourceRefreshModeAction.execute(datasource, RefreshModeTypes.Server, null, true, false))
              .then(() => createSelfServiceScheduleExtractAction(action))
              .then(() => getAppStore().dispatch(incrementRouterSpaceReactKey()));
          }
        } else {
          return displaySuccessDialog(workbook, datasources.items, PostPublishStep.Schedule)
            .then(() => changeDSRefreshMode(datasource, RefreshModeTypes.Server))
            .then(() => CreateDatasourceExtractTaskActionService.forDatasource(datasource, CreateExtractTasksMode.AllowScheduleOnServerAndClient))
            .then(() => getAppStore().dispatch(incrementRouterSpaceReactKey()));
        }

      } else if (!shouldRefreshWorkbook && serverRefreshDatasources.length === 0 && remoteRefreshDatasources.length === 0 && remoteLiveSyncDatasources.length >= 1) {
        // Case 5: There are just live query sync datasources so show the corresponding messages

        return displaySuccessDialog(workbook, datasources.items, PostPublishStep.LiveQuerySync);

      } else {
        // Case 6: More than one thing needs a schedule, so show the tabbed schedule interface with those things.

        return displaySuccessDialog(workbook, datasources.items, PostPublishStep.Schedule)
          .then(() => {
            const isExtractSSSEnabledForServer = FeatureFlags.selfServiceSchedulesForRefreshEnabled;
            const isExtractSSSEnabledForSite = SessionSiteSelectors.isSelfServiceSchedulesForRefreshEnabled(getAppStore().getState());

            // We do not want to show admin schedules option if SSS is enabled
            // A common schedules dialog will be shown for both server and remote refreshes in this multi datasource case
            if (isExtractSSSEnabledForServer && isExtractSSSEnabledForSite) {
              const allDataSources = serverRefreshDatasources.concat(remoteRefreshDatasources);
              let deferredPromise = new SimpleDeferred();
              RefreshSelfServiceScheduleDialog.open(CreateExtractTaskActionService.getRefreshSSSDialogParams(allDataSources)).then(result => deferredPromise.resolve(result))
                .catch(error => deferredPromise.reject(error));
              return deferredPromise.promise;
            }

            return displayTabbedScheduleDialogWithSSS(serverRefreshDatasources, remoteRefreshDatasources, shouldRefreshWorkbook, workbook);
          });
      }
    })
      .finally(() => {
        window.removeEventListener('unload', unloadListener, false);
        RemoteRefreshAgents.completeWorkflow(publishResult.remoteAgentWorkflowId);
      });
  }

  function displaySuccessDialog(workbook: IWorkbookDetailInfo, datasources: IDataSourceBase[], postPublishStep: PostPublishStep): Promise<any> {
    const orderClause = [Order.asc('index')];
    const viewsQuery = Views.forWorkbook(workbook).withOrder(orderClause);

    return viewsQuery.getCount()
      .then((viewCount) => viewsQuery.getSlice(0, viewCount))
      .then((result) => result.items)
      .then((viewList: IViewInfo[]) => {
        const dialogProps = getPublishCompleteDialogProps(viewList, workbook, datasources, postPublishStep);
        Telemetry.sendEvent<PublishCompleteDialogDisplayedEvent>({
          type: TelemetryEvents.PublishCompleteDialogDisplayed,
          isPreviewDeviceLayoutsLinkDisplayed: dialogProps.shouldDisplayDevicePreview
        });

        return buildPublishSuccessDialogFromProps(dialogProps).result;
      }) as Promise<any>;
  }

  export function buildPublishSuccessDialogFromProps(props: PublishCompleteDialogProps): ModalInstance<unknown> {
    dialogWithPreviewModalInstance = ReactModalService.open({
      type: DialogType.NormalWithTitleBar,
      title: PublishCompleteDialog.Title(),
      dialogContent: React.createElement(PublishCompleteDialog.Content, props),
      footerContent: React.createElement(Provider, { store: getAppStore() },
        React.createElement(PublishCompleteDialog.ConnectedFooter, props)
      ),
      testId: 'publish-workbook-success-modal',
      dialogWidth: 550,
      showCloseButton: true,
      dismissDialogOnCloseAction: true // Must dismiss here to prevent the schedule dialog from opening
    });

    return dialogWithPreviewModalInstance;
  }

  export function getPublishCompleteDialogProps(
    viewList: IViewInfo[],
    workbook: IWorkbookDetailInfo,
    datasources: IDataSourceBase[],
    postPublishStep: PostPublishStep): PublishCompleteDialogProps {
    let publishLiveQueryInfo = '';
    if (postPublishStep === PostPublishStep.LiveQuerySync) {
      publishLiveQueryInfo = getLiveQueryInfoText(datasources);
    }

    return {
      // We need to use a member variable here because the caller of this method will mutate this modal instance
      // after the dialog is displayed. We need this mutation to propagate to these functions, so we store the
      // instance where we can retrieve it later.
      dismiss: () => dialogWithPreviewModalInstance && dialogWithPreviewModalInstance.dismiss(),
      close: () => dialogWithPreviewModalInstance && dialogWithPreviewModalInstance.close(0),
      onDevicePreviewClick: () => openDevicePreviewForViz(viewList[0].path, dialogWithPreviewModalInstance),
      shareContent: shareContent,
      workbook: workbook,
      datasources: datasources,
      getDatasourceIconClass: getDatasourceIconClass,
      publishLiveQueryInfo: publishLiveQueryInfo,
      shouldDisplayExtensionServiceSection: FeatureFlags.analyticsExtensionsNamedConnections,
      shouldDisplayDevicePreview: viewList.length > 0,
      shouldDisplayLiveQueryInfo: postPublishStep === PostPublishStep.LiveQuerySync,
      shouldDisplayScheduleButton: postPublishStep === PostPublishStep.Schedule,
    };
  }

  function openDevicePreviewForViz(vizPath: string, publishCompleteModalInstance: ModalInstance<unknown>) {
    Telemetry.sendEvent({
      type: TelemetryEvents.PreviewDeviceLayoutsLinkClicked
    });

    DevicePreviewModalBuilder.openPreview(vizPath);
    publishCompleteModalInstance.dismiss();
  }

  function getLiveQueryInfoText(datasources: IDataSourceBase[]) {
    const datasourceNames = datasources.map(function(datasource) { return datasource.name; }).join(',');
    return Messages.publishWorkbookSuccessLiveQueryInfo({ 'ITEM_NAME': L10n.elideIfLong(datasourceNames, 50) });
  }

  function displayTabbedScheduleDialogWithSSS(serverRefreshDatasources: IDataSourceBase[], remoteRefreshDatasources: IDataSourceBase[], shouldRefreshWorkbook: boolean, workbook?: IWorkbookDetailInfo,) {
    let schedulesQuery = SearchSuggestionsService.schedulesQuery(ScheduleActions.Extract);
    SliceableHelper.fetchAll(schedulesQuery).then(schedulesResult => {

      let dialogProps: Omit<TabbedScheduleDialogWithSSSProps, 'close'> = {
        workbookTab: null,
        datasourceTabs: [],
        scheduleOptions: schedulesResult.items,
        setSchedules: (workbookTab: IWorkbookTab, datasourceTabs: IDatasourceTab[]) => {
          const promises: Promise<IResult>[] = [];
          const itemNames: string[] = [];

          // Create workbook extract task
          if (workbookTab && workbookTab.extractTask && workbookTab.extractTask.schedule) {
            promises.push(Workbooks.createExtractTask([workbookTab.workbook.id], workbookTab.extractTask.schedule.id, workbookTab.extractTask.type));
            itemNames.push(workbookTab.workbook.name);
          }

          // Create datasource refresh extract tasks
          datasourceTabs.forEach((datasourceTab: IDatasourceTab) => {
            if (datasourceTab.extractTask && datasourceTab.extractTask.schedule) {
              promises.push(DataSources.createExtractTask([datasourceTab.datasource.id], datasourceTab.extractTask.schedule.id, datasourceTab.extractTask.type));
              itemNames.push(datasourceTab.datasource.name);
            }
          });

          Promise.all(promises).then(() => {
            if (itemNames.length > 0) {
              const itemNamesStr = formatListAsString(itemNames);
              ToasterService.instance.pop({ type: ToastType.Success, text: Messages.publishSchedulesReady({ ITEM_NAMES: itemNamesStr, ITEM_COUNT: itemNames.length }) });
            }
          });
        }
      };

      // Build a tab for the workbook, if there is one
      if (workbook && shouldRefreshWorkbook) {
        dialogProps.workbookTab = {
          workbook: workbook,
          iconClassFn: () => 'tb-data-pane-workbook-icon'
        };
      }

      // Build a tab for every server-refresh datasource, if there are any
      if (serverRefreshDatasources) {
        _.forEach(serverRefreshDatasources, (datasource: IDataSourceBase) => {
          const datasourceTab: IDatasourceTab = {
            datasource: datasource,
            iconClassFn: () => getDatasourceIconClass(datasource)
          };
          dialogProps.datasourceTabs.push(datasourceTab);
        });
      }

      // Build a tab for every remote-refresh datasource, if there are any
      if (remoteRefreshDatasources) {
        _.forEach(remoteRefreshDatasources, (datasource: IDataSourceBase) => {
          const datasourceTab: IDatasourceTab = {
            datasource: datasource,
            iconClassFn: () => getDatasourceIconClass(datasource)
          };
          dialogProps.datasourceTabs.push(datasourceTab);
        });
      }

      const dialog = openTabbedScheduleDialogWithSSS(dialogProps);
      return dialog.result;
    });
  }

  function getWorkbookExtractTaskCount(workbook: IWorkbookDetailInfo): Promise<number> {
    let query = ExtractTasks.all();
    query = query.addFilter(Filter.fieldEq(FilterFieldNames.workbookId, workbook.id));
    return query.getCount() as Promise<number>;
  }

  function changeDSRefreshMode(datasource: IDataSourceBase, refreshMode: string) {
    // Changing refresh modes causes an error on on-prem servers, so only do it if datasync is an option.
    if (ServerSettingsUnauthenticated.isBridgeEnabled()) {
      return ChangeDatasourceRefreshModeAction.execute(datasource, refreshMode, null, true, false);
    } else {
      return Promise.resolve({});
    }
  }

  function getDatasourceIconClass(datasource: IDataSourceBase): string {
    if (!datasource.hasExtracts && !datasource.isHierarchical) {
      return 'tb-data-pane-datasource-icon';
    } else if (datasource.hasExtracts && !datasource.isHierarchical) {
      return 'tb-data-pane-datasource-extract-icon';
    } else {
      return 'tb-data-pane-datasource-cube-icon';
    }
  }
}

export type PublishWorkbookAction = typeof PublishWorkbookAction;
